import { Directive, ElementRef, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { takeUntil } from 'rxjs/operators';
import { fromEvent, Subject } from 'rxjs';

const SCROLL_GAP: number = 15;

@Directive({
    selector: '[finderScrollTo]',
    standalone: true
})
export class ScrollToDirective implements AfterViewInit, OnDestroy {
    private scrollId: string;
    private destroy$ = new Subject<void>();

    @Input()
    set finderScrollTo(id: string) {
        this.scrollId = id;
    }

    @Input() finderScrollToId: string;

    constructor(
        private elementRef: ElementRef,
        private route: ActivatedRoute,
        private windowRef: WindowRef,
    ) {}

    ngAfterViewInit() {
        // Set the current element ID to the provided `scrollId` value
        this.elementRef.nativeElement.id = this.scrollId;
        this.scrollCheck();
    }

    scrollToElement(element, behavior: ScrollBehavior = 'instant') {
        // avoid browser scroll to last position in the web app
        history.scrollRestoration = 'manual';

        /**
         * we can't use offsetTop. This only gives the distance to the next parent.
         * if it's nested then the value can be 0.
         *
         * getBoundingClientRect will return the distance to the top of the page
         * but if the scrollbar isn't at the top of the page we'll get the wrong
         * value. including the scrollbar position makes up for this
         * https://stackoverflow.com/a/33840267/122236
         *
         * After getting the distance between the element to the top of the page
         * we need to calculate the scrolling offset if we have sticky navitagion
         * components in the DOM (either desktop or mobile).
         *
         * With the querySelector and offsetHeight property we can get their height
         * and calcualte totalOffset. If we want to set a gap between the scroll
         * target and the viewport we can add SCROLL_GAP.
         *
         * Finally we can calculate the total scrollTargetY.
         */

        const elementTop: number = element.getBoundingClientRect().top;
        const currentScrollY: number = this.windowRef.nativeWindow.scrollY;

        const navigationHeight: number = (document.querySelector('finder-content-navigation') as HTMLElement)?.offsetHeight || 0;
        const mobileNavigationHeight: number = (document.querySelector('finder-mobile-content-navigation') as HTMLElement)?.offsetHeight || 0;

        const totalOffset: number = navigationHeight + mobileNavigationHeight + SCROLL_GAP;
        const scrollTargetY: number = elementTop + currentScrollY - totalOffset;

        window.scrollTo({ top: scrollTargetY, behavior });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /* istanbul ignore next */
    private scrollCheck() {
        this.route.fragment
            .pipe(takeUntil(this.destroy$))
            .subscribe((fragment) => {

                if (this.scrollId === fragment) {
                    // Window
                    const window = this.windowRef.nativeWindow;

                    // Set element
                    let el = this.elementRef.nativeElement;

                    // If we defined a parent element, try to set that as the target element
                    // If we messed up the selector in some way, fall back to this element
                    if (this.finderScrollToId) {
                        el = window.document.querySelector(`#${this.finderScrollToId}`) ||
                            this.elementRef.nativeElement;
                    }

                    // If all resources in the page are already loaded at this point
                    // like header/footer and lazy loaded images
                    // we just scroll to the correct position
                    this.scrollToElement(el);

                    // If resources are still loading listen to the load event
                    // so we can adjust the scroll position once everything is loaded.
                    // with requestAnimationFrame() we can ensure page is loaded and rendered
                    fromEvent(window, 'load')
                        .pipe(
                            takeUntil(this.destroy$)
                        )
                        .subscribe({
                            next: () => window.requestAnimationFrame(() => this.scrollToElement(el, 'smooth'))
                        });
                }
            });
    }
}
